import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, Truck, CreditCard } from "lucide-react";
import { api, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { MaskLines } from "@/components/site/Reveal";

export default function ProductPage() {
  const { slug } = useParams();
  const { add } = useCart();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [img, setImg] = useState(0);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setP(null); setErr(false); setImg(0); setQty(1);
    api.get(`/products/${slug}`).then((r) => setP(r.data)).catch(() => setErr(true));
  }, [slug]);

  if (err) return <div className="py-40 text-center" data-testid="product-not-found"><p className="font-display text-4xl">Product not found.</p><Link to="/shop" className="link-u mt-4 inline-block text-sm font-semibold">Back to shop</Link></div>;
  if (!p) return <div className="min-h-[70vh]" />;
  const imgs = [p.image, p.alt_image].filter(Boolean);

  return (
    <div className="mx-auto grid max-w-[1440px] gap-10 px-5 pb-32 pt-8 md:px-10 lg:grid-cols-12 lg:gap-16 lg:pt-14" data-testid="product-page">
      <div className="lg:col-span-7">
        <motion.div initial={{ clipPath: "inset(0 0 100% 0 round 24px)" }} animate={{ clipPath: "inset(0 0 0% 0 round 24px)" }} transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }} className="relative aspect-[4/5] overflow-hidden rounded-3xl bg-[var(--sand)]">
          {imgs.map((src, i) => (
            <img key={src} src={src} alt={p.name} className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${i === img ? "opacity-100" : "opacity-0"}`} />
          ))}
        </motion.div>
        {imgs.length > 1 && (
          <div className="mt-4 flex gap-3">
            {imgs.map((src, i) => (
              <button key={src} onClick={() => setImg(i)} className={`h-24 w-20 overflow-hidden rounded-xl border-2 transition-colors ${i === img ? "border-[var(--terra)]" : "border-transparent"}`} data-testid={`product-thumb-${i}`}>
                <img src={src} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="lg:col-span-5 lg:sticky lg:top-28 lg:self-start">
        <p className="eyebrow text-[var(--terra-deep)]">{p.category}{p.badge ? ` · ${p.badge}` : ""}</p>
        <MaskLines as="h1" testid="product-title" className="font-display mt-3 text-4xl leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl" lines={[{ text: p.name }]} />
        <div className="mt-6 flex items-baseline gap-3">
          <span className="text-2xl font-semibold" data-testid="product-price">{money(p.price)}</span>
          {p.compare_at && <span className="text-[var(--ink-soft)] line-through">{money(p.compare_at)}</span>}
        </div>
        <p className="mt-6 text-base leading-relaxed text-[var(--ink-soft)]">{p.description}</p>
        <p className={`mt-6 text-sm font-medium ${p.stock < 10 ? "text-[var(--terra-deep)]" : "text-[var(--ink-soft)]"}`} data-testid="product-stock">
          {p.stock <= 0 ? "Sold out" : p.stock < 10 ? `Only ${p.stock} left` : "In stock, ready to ship"}
        </p>
        <div className="mt-8 flex gap-3">
          <div className="flex items-center rounded-full border border-[var(--ink)]">
            <button className="grid h-14 w-12 place-items-center" onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="product-qty-decrease"><Minus size={16} /></button>
            <span className="w-6 text-center font-semibold" data-testid="product-qty">{qty}</span>
            <button className="grid h-14 w-12 place-items-center" onClick={() => setQty((q) => Math.min(p.stock, q + 1))} data-testid="product-qty-increase"><Plus size={16} /></button>
          </div>
          <motion.button whileTap={{ scale: 0.96 }} disabled={p.stock <= 0} onClick={() => add(p, qty)} className="flex-1 rounded-full bg-[var(--ink)] text-sm font-semibold text-[var(--bone)] transition-colors hover:bg-[var(--terra)] disabled:opacity-40" data-testid="add-to-cart-button">
            Add to bag — {money(p.price * qty)}
          </motion.button>
        </div>
        <div className="mt-10 space-y-4 border-t border-[var(--line)] pt-8 text-sm text-[var(--ink-soft)]">
          <p className="flex items-center gap-3"><Truck size={18} className="text-[var(--ink)]" /> Free shipping on orders over $150</p>
          <p className="flex items-center gap-3"><CreditCard size={18} className="text-[var(--ink)]" /> Pay securely with debit or credit card</p>
        </div>
      </div>
    </div>
  );
}
